import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';

import {
  BulkVerificationRunError,
  BulkVerificationRunPreview,
  BulkVerificationRunRefusal,
  VerificationCredits,
} from 'api/bitedb/entities/bulkVerificationRun.service';
import BulkVerificationCredits from './BulkVerificationCredits';
import { BULK_VERIFICATION_TILE_COPY } from './bulkVerificationTile';
import { formatCount, plural } from './bulkVerificationFormat';
import { BulkVerificationScopeChoice } from './BulkVerificationScopeStep';

export type BulkVerificationPreviewState =
  | { status: 'loading' }
  | { status: 'ready'; preview: BulkVerificationRunPreview }
  | { status: 'error'; error: BulkVerificationRunError };

export type BulkVerificationStartFailure =
  | { kind: 'credits-changed' }
  | { kind: 'refused'; error: BulkVerificationRunError };

/** Refusals that trying again cannot get past, with the copy that explains each. */
const GATE_COPY: Partial<
  Record<BulkVerificationRunRefusal, { title: string; text: string }>
> = {
  'nothing-to-verify': {
    title: 'Nothing left to verify',
    text: 'Nothing in this selection can be verified any more.',
  },
  'addon-required': {
    title: 'Business verification is not on your plan',
    text: "It isn't included in your team's plan. Contact sales to add it.",
  },
  'database-off': {
    title: BULK_VERIFICATION_TILE_COPY['database-off'].title,
    text: BULK_VERIFICATION_TILE_COPY['database-off'].body,
  },
  'verification-disabled': {
    title: 'Business verification is switched off',
    text: "It's been switched off for the moment, so no run can start.",
  },
  'not-found': {
    title: "This database can't be found",
    text: 'It may have been deleted, or you may no longer have access to it.',
  },
};

const RETRYABLE: ReadonlySet<BulkVerificationRunRefusal> = new Set([
  'unavailable',
  'unreachable',
]);

export const isGateRefusal = (refusal: BulkVerificationRunRefusal): boolean =>
  Boolean(GATE_COPY[refusal]);

type RefusalPhase = 'preview' | 'start';

const PHASE_COPY: Record<
  RefusalPhase,
  { title: string; retryText: string; charged: string }
> = {
  preview: {
    title: "Couldn't work out the cost",
    retryText: "The verification service didn't answer.",
    charged: 'Nothing has been charged.',
  },
  start: {
    title: "Couldn't start the run",
    retryText: 'The verification service is unavailable right now.',
    charged: 'Nothing was charged.',
  },
};

const refusalPanel = (
  phase: RefusalPhase,
  error: BulkVerificationRunError,
): { title: string; text: string; retryable: boolean } => {
  const copy = PHASE_COPY[phase];
  const gate = GATE_COPY[error.refusal];
  if (gate) {
    return {
      title: gate.title,
      text: `${gate.text} ${copy.charged}`,
      retryable: false,
    };
  }
  if (RETRYABLE.has(error.refusal)) {
    return {
      title: copy.title,
      text: `${copy.retryText} ${copy.charged}`,
      retryable: true,
    };
  }
  return {
    title: copy.title,
    text: `${error.message} ${copy.charged}`,
    retryable: false,
  };
};

interface BulkVerificationReviewStepProps {
  databaseName: string;
  scope: BulkVerificationScopeChoice;
  /** How many entities the scope covers, for the loading line. */
  scopeCount?: number;
  previewState: BulkVerificationPreviewState;
  credits: VerificationCredits | null;
  startFailure: BulkVerificationStartFailure | null;
  onRetryPreview: () => void;
  onRetryStart: () => void;
  onChangeSelection: () => void;
  onViewCreditPlans: () => void;
}

const CHECKS_THAT_RUN = [
  'Domain age',
  'Domain reputation',
  'Email',
  'Phone',
  'Website quality',
  'Business location',
  'Canadian registries',
];

const ErrorPanel: React.FC<{
  title: string;
  text: string;
  onRetry?: () => void;
}> = ({ title, text, onRetry }) => (
  <div className='bdb-bulk-verify-error' role='alert'>
    <ErrorOutlineIcon
      className='bdb-bulk-verify-error-icon'
      aria-hidden='true'
    />
    <div className='bdb-bulk-verify-error-text'>
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
    {onRetry && (
      <button
        type='button'
        className='btn-outline bdb-bulk-verify-btn-sm'
        onClick={onRetry}
      >
        Try again
      </button>
    )}
  </div>
);

/** Step 2: what the run covers and costs, from the no-reservation preview. */
const BulkVerificationReviewStep: React.FC<BulkVerificationReviewStepProps> = ({
  databaseName,
  scope,
  scopeCount,
  previewState,
  credits,
  startFailure,
  onRetryPreview,
  onRetryStart,
  onChangeSelection,
  onViewCreditPlans,
}) => {
  const [namesOpen, setNamesOpen] = useState(false);

  if (previewState.status === 'loading') {
    return (
      <div className='bdb-bulk-verify-counting' aria-busy='true'>
        <CircularProgress size={16} sx={{ color: 'var(--action-color)' }} />
        <span>
          Counting credits for
          {scopeCount !== undefined
            ? ` ${formatCount(scopeCount)} ${plural(
                scopeCount,
                'entity',
                'entities',
              )}`
            : ' this run'}
          …
        </span>
      </div>
    );
  }

  if (previewState.status === 'error') {
    const panel = refusalPanel('preview', previewState.error);
    return (
      <ErrorPanel
        title={panel.title}
        text={panel.text}
        onRetry={panel.retryable ? onRetryPreview : undefined}
      />
    );
  }

  const { preview } = previewState;
  const nothing = preview.nothingToVerify;
  const hiddenNames = nothing.count - nothing.records.length;
  const startPanel =
    startFailure?.kind === 'refused'
      ? refusalPanel('start', startFailure.error)
      : null;

  return (
    <>
      {startFailure?.kind === 'credits-changed' && (
        <div className='bdb-bulk-verify-notice' role='status'>
          <InfoOutlinedIcon
            className='bdb-bulk-verify-notice-icon'
            aria-hidden='true'
          />
          <span>
            Credits changed since the estimate. Updated with the latest numbers.
          </span>
        </div>
      )}
      {startPanel && (
        <ErrorPanel
          title={startPanel.title}
          text={startPanel.text}
          onRetry={startPanel.retryable ? onRetryStart : undefined}
        />
      )}
      <div className='bdb-bulk-verify-review'>
        <section className='bdb-bulk-verify-box' aria-label='This run'>
          <div className='bdb-bulk-verify-label'>This run</div>
          <div className='bdb-bulk-verify-verdict'>
            Verify {formatCount(preview.toVerify)}{' '}
            {plural(preview.toVerify, 'entity', 'entities')}
          </div>
          <p className='bdb-bulk-verify-lead'>
            {scope === 'all'
              ? `Every entity in ${databaseName} (${formatCount(preview.total)}).`
              : `From your selection of ${formatCount(
                  preview.total,
                )} in ${databaseName}.`}
          </p>
          <div className='bdb-bulk-verify-line'>
            <span className='bdb-bulk-verify-line-title'>
              {formatCount(preview.toVerify)} will be verified
            </span>
            <span className='bdb-bulk-verify-line-value'>
              {formatCount(preview.creditsRequired)}{' '}
              {plural(preview.creditsRequired, 'credit', 'credits')}
            </span>
            <span className='bdb-bulk-verify-line-text'>
              {preview.creditsRequired === preview.toVerify
                ? '1 credit each. '
                : ''}
              Each entity&apos;s current verification results are replaced.
            </span>
          </div>
          {nothing.count > 0 && (
            <div className='bdb-bulk-verify-line'>
              <span className='bdb-bulk-verify-line-title'>
                {formatCount(nothing.count)}{' '}
                {plural(nothing.count, 'has', 'have')} nothing to verify
              </span>
              <span className='bdb-bulk-verify-line-value bdb-bulk-verify-muted'>
                No charge
              </span>
              <span className='bdb-bulk-verify-line-text'>
                {plural(nothing.count, 'It has', 'They have')} no details that
                can be checked, so {plural(nothing.count, "it's", "they're")}{' '}
                skipped.{' '}
                <button
                  type='button'
                  className='bdb-bulk-verify-link'
                  aria-expanded={namesOpen}
                  aria-controls='bdb-bulk-verify-names'
                  onClick={() => setNamesOpen((open) => !open)}
                >
                  {namesOpen ? 'Hide names' : 'Show names'}
                </button>
              </span>
              {namesOpen && (
                <div
                  id='bdb-bulk-verify-names'
                  className='bdb-bulk-verify-names'
                >
                  <ul>
                    {nothing.records.map((record) => (
                      <li key={record.databaseEntityId}>
                        {record.name || 'Unnamed entity'}
                      </li>
                    ))}
                  </ul>
                  {hiddenNames > 0 && (
                    <div className='bdb-bulk-verify-muted'>
                      and {formatCount(hiddenNames)} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className='bdb-bulk-verify-line bdb-bulk-verify-line-block'>
            <span className='bdb-bulk-verify-line-title'>What runs</span>
            <ul className='bdb-bulk-verify-checks'>
              {CHECKS_THAT_RUN.map((check) => (
                <li key={check}>{check}</li>
              ))}
            </ul>
            <ul className='bdb-bulk-verify-fine'>
              <li>
                <ReplayOutlinedIcon aria-hidden='true' />
                <span>
                  If no check completes for an entity, its credit comes back.
                </span>
              </li>
              <li>
                <ScheduleOutlinedIcon aria-hidden='true' />
                <span>Runs in the background. You can close this window.</span>
              </li>
            </ul>
          </div>
        </section>
        <BulkVerificationCredits
          creditsRequired={preview.creditsRequired}
          creditsRemaining={preview.creditsRemaining}
          credits={credits}
          onChangeSelection={onChangeSelection}
          onViewCreditPlans={onViewCreditPlans}
        />
      </div>
    </>
  );
};

export default BulkVerificationReviewStep;
